import dayjs from 'dayjs';
import { mapParamsBySchema, toPreviewSrc } from '../../../util';
import { apiRequest } from '../../../Services/Common/ApiRequest';
import Global from '../../../Services/Common/Global';
import { M_PEA_EDU_LOG_DETAIL_Schema } from './M_PEA_EDU_LOG_DETAIL_Schema';

const Pick = (row, ...keys) => {
  for (const k of keys) {
    if (row[k] != null && row[k] !== '') return row[k];
  }
  return '';
};

export const StripEduHtmlToText = (value) => {
  if (!value) return '';
  let text = value.toString();
  text = text.replace(/<\/p>\s*<br\s*\/?>\s*<p[^>]*>/gi, '\n');
  text = text.replace(/<\/p>\s*<p[^>]*>/gi, '\n');
  text = text.replace(/<br\s*\/?>/gi, '\n');
  text = text.replace(/<\/?p[^>]*>/gi, '');
  text = text.replace(/<\/?[a-z][^>]*>/gi, '');
  text = text.replace(/&nbsp;/gi, ' ');
  text = text.replace(/\r\n|\r/g, '\n');
  text = text.replace(/\n{2,}/g, '\n');
  text = text.replace(/^\n+/, '');
  return text.trim();
};

const StatusHeaderVariant = (status) => {
  const s = String(status || '');
  if (s.includes('반려')) return 'wait';
  if (s.includes('완료')) return 'ok';
  if (s.includes('요청') || s.includes('대기')) return 'pending';
  return 'pending';
};

const ToContentBullets = (text) => {
  const t = StripEduHtmlToText(text);
  if (!t) return [];
  const byNl = t
    .split(/\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (byNl.length > 1) return byNl;
  return [t];
};

/**
 * GetTraCustom02R1 교육 현장 사진 경로 → 미리보기 URL
 * WOR_BASIC_02 `BuildPreview`(경로 정규화) + `toPreviewSrc`(Commons.EncodeUrl) 와 동일 계열
 */
const ToTraCustom02ImagePreviewSrc = (rawPath) => {
  const normalized = String(rawPath ?? '')
    .trim()
    .replace(/\\/g, '/');
  if (!normalized) return '';
  return toPreviewSrc(normalized);
};

/** R1 별칭 컬럼 — EduImageFileName/SaveName/Path 1·2, 자체교육 사진 */
const EDU_SITE_IMAGE_SLOTS = [
  {
    pathKeys: [
      'eduImageFilePath1',
      'EduImageFilePath1',
      'EDU_IMAGE_FILE_PATH1',
    ],
    fileNameKeys: [
      'eduImageFileName1',
      'EduImageFileName1',
      'EDU_IMAGE_FILE_NAME1',
    ],
    saveNameKeys: [
      'eduImageSaveName1',
      'EduImageSaveName1',
      'EDU_IMAGE_SAVE_NAME1',
    ],
    defaultLabel: '교육 현장 사진 1',
  },
  {
    pathKeys: [
      'eduImageFilePath2',
      'EduImageFilePath2',
      'EDU_IMAGE_FILE_PATH2',
    ],
    fileNameKeys: [
      'eduImageFileName2',
      'EduImageFileName2',
      'EDU_IMAGE_FILE_NAME2',
    ],
    saveNameKeys: [
      'eduImageSaveName2',
      'EduImageSaveName2',
      'EDU_IMAGE_SAVE_NAME2',
    ],
    defaultLabel: '교육 현장 사진 2',
  },
];

const BuildTraCustom02EduSiteImages = (apiRow) => {
  const list = [];
  for (const slot of EDU_SITE_IMAGE_SLOTS) {
    const pathRaw = Pick(apiRow, ...slot.pathKeys);
    if (!String(pathRaw ?? '').trim()) continue;
    const src = ToTraCustom02ImagePreviewSrc(pathRaw);
    if (!src) continue;
    const fileName = Pick(apiRow, ...slot.fileNameKeys);
    const saveName = Pick(apiRow, ...slot.saveNameKeys);
    const label =
      String(fileName || '').trim() ||
      String(saveName || '').trim() ||
      slot.defaultLabel;
    list.push({
      src,
      alt: label,
      caption: label,
      filePath: String(pathRaw).trim().replace(/\\/g, '/'),
      fileName: String(fileName || '').trim(),
      saveName: String(saveName || '').trim(),
    });
  }
  const sp = Pick(
    apiRow,
    'selfImageFilePath',
    'SelfImageFilePath',
    'SELF_IMAGE_FILE_PATH'
  );
  if (String(sp ?? '').trim()) {
    const src = ToTraCustom02ImagePreviewSrc(sp);
    if (src) {
      const sf = Pick(
        apiRow,
        'selfImageFileName',
        'SelfImageFileName',
        'SELF_IMAGE_FILE_NAME'
      );
      const ss = Pick(
        apiRow,
        'selfImageSaveName',
        'SelfImageSaveName',
        'SELF_IMAGE_SAVE_NAME'
      );
      const label =
        String(sf || '').trim() || String(ss || '').trim() || '자체교육 사진';
      list.push({
        src,
        alt: label,
        caption: label,
        filePath: String(sp).trim().replace(/\\/g, '/'),
        fileName: String(sf || '').trim(),
        saveName: String(ss || '').trim(),
      });
    }
  }
  return list;
};

/**
 * GetTraCustom02R2 참석자 행 → 상세 목록
 */
export const SearchLogicPeaEduLogAttendees1 = (memberRows) => {
  if (!Array.isArray(memberRows) || memberRows.length === 0) return [];
  if (memberRows[0]?.type === 'Error') return [];

  return memberRows.map((m) => {
    const name = Pick(m, 'userNm', 'USER_NM', 'UserNm') || '이름 없음';
    const job = Pick(m, 'jobType', 'JOB_TYPE', 'JobType');
    const remark = Pick(m, 'remark', 'REMARK', 'Remark');
    const signPath = Pick(m, 'signPath', 'SIGN_PATH', 'SignPath');
    const signed =
      (signPath && String(signPath).trim() !== '') ||
      String(remark).includes('서명');

    const depNm = Pick(m, 'depNm', 'DEP_NM', 'DeptNm', 'deptNm');
    return {
      initial: String(name).charAt(0) || '?',
      name,
      dept: depNm || job || '-',
      signed,
    };
  });
};

/**
 * GetTraCustom02R1 마스터 1건 + 목록행 보조 → 상세 화면 모델
 */
export const SearchLogicPeaEduLogDetail1 = (apiRow, listRow, attendeeRows) => {
  if (!apiRow) return null;

  const eduDt = Pick(apiRow, 'eduDt', 'EduDt');
  const eduDate = eduDt ? dayjs(eduDt).format('YYYY-MM-DD') : '';
  const createNm =
    Pick(apiRow, 'createNm', 'CreateNm') ||
    listRow?.reqUserNm ||
    listRow?.ReqUserNm ||
    '';
  const authorDept =
    listRow?.reqDeptNm ||
    listRow?.ReqDeptNm ||
    Pick(apiRow, 'eduInstructorDepNm', 'EduInstructorDepNm') ||
    '-';

  const eduTypeNm = StripEduHtmlToText(Pick(apiRow, 'eduTypeNm', 'EduTypeNm'));
  const eduNmNm = StripEduHtmlToText(Pick(apiRow, 'eduNmNm', 'EduNmNm'));
  /** 상단 「교육의 구분」 카드 — eduTypeNm 만 표시 */
  const categoryLabel = eduTypeNm || '교육 구분';

  const eduContentsNm = Pick(apiRow, 'eduContentsNm', 'EduContentsNm');
  const contentBullets = ToContentBullets(eduContentsNm);

  const status = Pick(apiRow, 'status', 'Status') || '대기';
  const tpmId = Pick(apiRow, 'tpmId', 'TpmId');

  const eduPlace = StripEduHtmlToText(Pick(apiRow, 'eduPlace', 'EduPlace'));
  const eduManager = StripEduHtmlToText(
    Pick(apiRow, 'eduManager', 'EduManager')
  );
  const eduTm = Pick(apiRow, 'eduTm', 'EduTm') || '-';
  const instructorNm = Pick(apiRow, 'eduInstructorNm', 'EduInstructorNm');
  const instructorPos = Pick(
    apiRow,
    'eduInstructorPosition',
    'EduInstructorPosition'
  );
  const instructorDep = Pick(
    apiRow,
    'eduInstructorDepNm',
    'EduInstructorDepNm'
  );
  const instructorLine = [instructorNm, instructorPos]
    .filter(Boolean)
    .join(' ');
  const instructorSub = [instructorDep].filter(Boolean).join(' ');

  const tm = Number(Pick(apiRow, 'eduTargetMCnt', 'EduTargetMCnt')) || 0;
  const tw = Number(Pick(apiRow, 'eduTargetWCnt', 'EduTargetWCnt')) || 0;
  const tt = Number(Pick(apiRow, 'eduTargetCnt', 'EduTargetCnt')) || 0;
  const em = Number(Pick(apiRow, 'eduExecMCnt', 'EduExecMCnt')) || 0;
  const ew = Number(Pick(apiRow, 'eduExecWCnt', 'EduExecWCnt')) || 0;
  const et = Number(Pick(apiRow, 'eduExecCnt', 'EduExecCnt')) || 0;
  const nm = Number(Pick(apiRow, 'eduNoExecMCnt', 'EduNoExecMCnt')) || 0;
  const nw = Number(Pick(apiRow, 'eduNoExecWCnt', 'EduNoExecWCnt')) || 0;
  const nt = Number(Pick(apiRow, 'eduNoExecCnt', 'EduNoExecCnt')) || 0;

  const overview = {
    eduDate: eduDate || '-',
    eduTime: eduTm && eduTm !== '-' ? String(eduTm) : '-',
    eduPlace: eduPlace || '-',
    eduManager: eduManager || '-',
    instructorLine: instructorLine
      ? `${instructorLine}${instructorSub ? ` (${instructorSub})` : ''}`
      : '',
  };

  const r1 = StripEduHtmlToText(
    Pick(apiRow, 'eduNoExecReason1', 'EduNoExecReason1')
  );
  const r2 = StripEduHtmlToText(
    Pick(apiRow, 'eduNoExecReason2', 'EduNoExecReason2')
  );
  const r3 = StripEduHtmlToText(
    Pick(apiRow, 'eduNoExecReason3', 'EduNoExecReason3')
  );
  const absenceReasonText = [r1, r2, r3].filter(Boolean).join(' ').trim();

  const personnelTable = {
    male: { target: tm, conducted: em, absent: nm },
    female: { target: tw, conducted: ew, absent: nw },
    total: {
      target: tt > 0 ? tt : tm + tw,
      conducted: et > 0 ? et : em + ew,
      absent: nt > 0 ? nt : nm + nw,
    },
  };

  const images = BuildTraCustom02EduSiteImages(apiRow);

  const idStr = Pick(apiRow, 'midSignIdsJoined', 'MidSignIdsJoined');
  const pathStr = Pick(apiRow, 'midSignPathsJoined', 'MidSignPathsJoined');
  const ids = idStr
    ? String(idStr)
        .split('|')
        .filter((x) => x !== '')
    : [];
  const paths = pathStr ? String(pathStr).split('|') : [];

  const returnYnMst =
    String(
      Pick(apiRow, 'returnYn', 'ReturnYn', 'RETURN_YN') || ''
    )
      .trim()
      .toUpperCase() === 'Y';

  /** 중간 결재자별 표시 — TB_BO_TPA_MID_SIGN 서명 여부와 동일 (미서명 시 결재요청·서명 완료 시 결재완료) */
  const intermediate = ids.map((signId, i) => {
    const path = (paths[i] ?? '').trim();
    const done = path.length > 0;
    if (returnYnMst) {
      return {
        name: signId || `중간결재 ${i + 1}`,
        done: false,
        statusLabel: '결재대기',
        statusVariant: 'pending',
        time: '',
        subtitle: '반려 후 재상신 대기',
      };
    }
    return {
      name: signId || `중간결재 ${i + 1}`,
      done,
      statusLabel: done ? '결재완료' : '결재요청',
      statusVariant: done ? 'ok' : 'pending',
      time: '',
      subtitle: done ? '서명 완료' : '서명 대기',
    };
  });

  const unsignedMidCount = intermediate.filter((m) => !m.done).length;

  const finalId = Pick(apiRow, 'finalSignId', 'FinalSignId');
  const finalYn = Pick(apiRow, 'finalSignYn', 'FinalSignYn');
  const finalDone = finalYn === 'Y' || finalYn === 'y' || finalYn === true;

  /** 최종 결재 행 — MID 전원 서명 후 최종만 서명 가능 시 승인요청, 그 외 미완 시 결재대기, 반려·완료 동기화 */
  let finalStatusLabel = '결재대기';
  let finalStatusVariant = 'pending';
  if (finalDone) {
    finalStatusLabel = '결재완료';
    finalStatusVariant = 'ok';
  } else if (returnYnMst) {
    finalStatusLabel = '반려';
    finalStatusVariant = 'wait';
  } else if (unsignedMidCount === 0) {
    finalStatusLabel = '승인요청';
    finalStatusVariant = 'pending';
  } else {
    finalStatusLabel = '결재대기';
    finalStatusVariant = 'pending';
  }

  const attendees = SearchLogicPeaEduLogAttendees1(attendeeRows || []);

  return {
    docNo: String(tpmId || ''),
    statusLabel: status,
    statusHeaderVariant: StatusHeaderVariant(status),
    authorInitial: String(createNm).charAt(0) || '?',
    authorLine: createNm || '-',
    authorDept,
    eduDate: eduDate || '-',
    categoryLabel,
    categorySectionTitle: '교육의 구분',
    /** 헤더 카드 "총 교육 인원" — 실시(참석) 합계 우선, 없으면 대상 합계 */
    totalParticipants:
      personnelTable.total.conducted > 0
        ? personnelTable.total.conducted
        : personnelTable.total.target,
    eduTitle: eduNmNm || eduTypeNm || '교육일지',
    contentBullets:
      contentBullets.length > 0
        ? contentBullets
        : ['등록된 교육 내용이 없습니다.'],
    overview,
    personnelTable,
    absenceReasonText: (() => {
      if (absenceReasonText) return absenceReasonText;
      const hasAbsent = personnelTable.total.absent > 0;
      return hasAbsent
        ? '불참 사유가 등록되지 않았습니다.'
        : '불참자가 없습니다.';
    })(),
    specialNotes:
      StripEduHtmlToText(Pick(apiRow, 'significant', 'Significant')) ||
      '등록된 특이사항이 없습니다.',
    images,
    attendees,
    approvers: {
      intermediate,
      final: {
        name: finalId ? String(finalId) : '최종 결재자',
        done: finalDone,
        statusLabel: finalStatusLabel,
        statusVariant: finalStatusVariant,
      },
    },
  };
};

export const ParseTraCustom02R1Rows = (response) => {
  if (response == null) return { rows: [], errorMessage: null };
  let arr = [];
  if (Array.isArray(response)) {
    arr = response;
  } else if (Array.isArray(response.data)) {
    arr = response.data;
  }
  if (arr.length > 0 && arr[0]?.type === 'Error') {
    return { rows: [], errorMessage: arr[0].message || '조회 오류' };
  }
  return { rows: arr, errorMessage: null };
};

export const ParseTraCustom02R2Rows = (response) => {
  if (response == null) return [];
  let arr = [];
  if (Array.isArray(response)) {
    arr = response;
  } else if (Array.isArray(response.data)) {
    arr = response.data;
  }
  if (arr.length > 0 && arr[0]?.type === 'Error') return [];
  return arr;
};

/**
 * 교육일지 중간/최종 결재 서명 저장 (SaveTraCustom02ApproverSign)
 * 서버가 SaveTraCustom02ApproverSign_UpdateFinal 로 최종 서명 반영 시에도
 * 요청 본문은 TpmId·SignPath·UserId(결재자) 동일.
 */
export const SaveLogicPeaEduApprove1 = async ({
  tpmId,
  signPath,
  userIdPlain,
}) => {
  const payload = mapParamsBySchema(
    M_PEA_EDU_LOG_DETAIL_Schema.mapper.saveTraCustom02ApproverSign1,
    { tpmId, signPath, userIdPlain }
  );
  return apiRequest(
    'post',
    Global.urls.DocsManagement.saveTraCustom02ApproverSign,
    payload
  );
};

/**
 * 교육일지 중간/최종 결재 반려 (SaveTraCustom02ApproverReject)
 */
export const SaveLogicPeaEduReject1 = async ({
  tpmId,
  returnDetailPlain,
  userIdPlain,
}) => {
  const payload = mapParamsBySchema(
    M_PEA_EDU_LOG_DETAIL_Schema.mapper.saveTraCustom02ApproverReject1,
    { tpmId, returnDetailPlain, userIdPlain }
  );
  return apiRequest(
    'post',
    Global.urls.DocsManagement.saveTraCustom02ApproverReject,
    payload
  );
};
